import React, { useState, useEffect } from "react";
import Helmet from "react-helmet";
import CopyToClipboard from "react-copy-to-clipboard";
import { Modal } from "@wrstudios/components";
import { updateUser } from "../../api/user";
import { useSession } from "../../Session";
import Wrapper from "../common/Wrapper";
import Image from "../common/Image";
import Button from "../common/Button";
import CheckboxLabel from "../common/CheckboxLabel";
import Checkbox from "../common/Checkbox";
import Input from "../common/Input";
import NotFound from "../common/NotFound";
import IconEmbed from "../icons/material/action/IconEmbed";
import IconCheck from "../icons/material/navigation/IconCheck";
import IconArrowForward from "../icons/material/navigation/IconArrowForward";
import IconFacebook from "../icons/IconFacebook";
import IconTwitter from "../icons/IconTwitter";
import IconPinterest from "../icons/IconPinterest";
import wmhw from "./images/wmhw.png";
import {
  Container,
  Title,
  Section,
  SectionNotifications,
  Header,
  Text,
  ImageContainer,
  Tip,
  Grid,
  Cell,
  ShareButton,
  ShareButtonIconEmbed,
  ShareButtonIcon,
  ShareButtonText,
  Badge,
  ListContainer,
  List,
  ListItem,
  TryAttract,
  ModalContent,
  ModalFooter,
  Copy
} from "./styled/settings-lead-generation";

function useCopy() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setCopied(false);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [copied]);

  return [copied, setCopied];
}

function SettingsLeadGeneration() {
  const { features } = useSession();

  if (!features.hasAPIButton) {
    return <NotFound useNativeLink />;
  }

  return (
    <Container>
      <Helmet>
        <title>Lead Generation - Cloud CMA</title>
      </Helmet>
      <Wrapper>
        <Title>Lead Generation</Title>
        <div>
          <SettingsLeadGenerationLandingPage />
          <SettingsLeadGenerationNotifications />
          <SettingsLeadGenerationShare />
          <SettingsLeadGenerationAttract />
        </div>
      </Wrapper>
    </Container>
  );
}

function SettingsLeadGenerationLandingPage() {
  const { config } = useSession();
  const [copied, setCopied] = useCopy();

  return (
    <Section>
      <Header>Your Landing Page</Header>
      <Text align="center">
        Generate leads with your <strong>"What's my home worth?"</strong>{" "}
        landing page
      </Text>
      <ImageContainer>
        <a href={config.widgetUrl} target="_blank" rel="noreferrer">
          <Image src={wmhw} alt="What's my home worth?" />
        </a>
      </ImageContainer>
      <Text align="center">
        <Button app="cma" as="a" href={config.widgetUrl} target="_blank">
          View Landing Page
        </Button>
        <br />
        <CopyToClipboard text={config.widgetUrl} onCopy={() => setCopied(true)}>
          <Copy app="cma" variant="secondary">
            {copied ? "Link copied!" : "Copy link"}
          </Copy>
        </CopyToClipboard>
      </Text>
    </Section>
  );
}

function SettingsLeadGenerationNotifications() {
  const { currentUser, config, setConfig } = useSession();

  const handleChange = (e) => {
    const isChecked = e.target.checked;
    updateUser(currentUser.id, { auto_send_weblead_email: isChecked });
    setConfig((config) => ({ ...config, autoSendWebLeadEmail: isChecked }));
  };

  return (
    <SectionNotifications>
      <Header>Notifications</Header>
      <Text align="center">
        <CheckboxLabel>
          <Checkbox
            checked={config.autoSendWebLeadEmail}
            onChange={handleChange}
          />{" "}
          Automatically email a finished report to all of your web leads
        </CheckboxLabel>
        <Tip>
          If you uncheck this box, your web lead will not be sent an email, but
          you will still be notified via email.
        </Tip>
      </Text>
    </SectionNotifications>
  );
}

function SettingsLeadGenerationShare() {
  const { currentUser, config } = useSession();
  const [copied, setCopied] = useCopy();
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const socialTitle = encodeURIComponent(
    "What's my home worth? Get a free Consumer Market Analysis"
  );
  const socialMessage = encodeURIComponent(
    "Hey, I would like to send you a personalized Comparative Market Analysis (CMA) on your home."
  );
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    `${config.url}/api_widget/${currentUser.guid}/show?post_url=${config.url}&source_url=facebook.com")}&t=${socialTitle}`
  )}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${socialMessage}&url=${encodeURIComponent(
    `${config.url}/api_widget/${currentUser.guid}/show?post_url=${config.url}&source_url=twitter.com`
  )}&via=cloudcma`;
  const pinterestUrl = `http://pinterest.com/pin/create/button/?url=${encodeURIComponent(
    `${config.url}/api_widget/${currentUser.guid}/show?post_url=${config.url}`
  )}&media=${encodeURIComponent(
    currentUser.avatarUrl
  )}&description=${socialMessage} ${encodeURIComponent(
    `${config.url}/api_widget/${currentUser.guid}/show?post_url=${config.url}`
  )}`;
  const embedCode = `<script id=\"cloudcma_api_widget_script\" src=\"${config.url}/api_widget/${currentUser.guid}/new?format=js\"></script>`;

  return (
    <Section>
      <Header>Share</Header>
      <Grid>
        <Cell>
          <ShareButton as="button" onClick={() => setShowEmbedCode(true)}>
            <span>
              <ShareButtonIconEmbed>
                <IconEmbed />
              </ShareButtonIconEmbed>
              <ShareButtonText>Embed</ShareButtonText>
            </span>
          </ShareButton>
          {showEmbedCode && (
            <Modal onClose={() => setShowEmbedCode(false)}>
              <ModalContent>
                <Modal.Header>
                  Embed
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <Input as="textarea" rows="5" defaultValue={embedCode} />
                </Modal.Body>
                <ModalFooter>
                  <CopyToClipboard
                    text={embedCode}
                    onCopy={() => setCopied(true)}>
                    <Button app="cma">
                      {copied ? "Code Copied!" : "Copy Code"}
                    </Button>
                  </CopyToClipboard>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
        </Cell>
        <Cell>
          <ShareButton
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer">
            <span>
              <ShareButtonIcon>
                <IconFacebook />
              </ShareButtonIcon>
              <ShareButtonText>Facebook</ShareButtonText>
            </span>
          </ShareButton>
        </Cell>
        <Cell>
          <ShareButton
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer">
            <span>
              <ShareButtonIcon>
                <IconTwitter />
              </ShareButtonIcon>
              <ShareButtonText>Twitter</ShareButtonText>
            </span>
          </ShareButton>
        </Cell>
        <Cell>
          <ShareButton
            href={pinterestUrl}
            target="_blank"
            rel="noopener noreferrer">
            <span>
              <ShareButtonIcon>
                <IconPinterest />
              </ShareButtonIcon>
              <ShareButtonText>Pinterest</ShareButtonText>
            </span>
          </ShareButton>
        </Cell>
      </Grid>
    </Section>
  );
}

function SettingsLeadGenerationAttract() {
  const { config } = useSession();

  return (
    <Section>
      <Text align="center">
        <Badge>New</Badge>
      </Text>
      <Header>Want more out of your landing page?</Header>
      <ListContainer>
        <List>
          <ListItem>
            <IconCheck />
            Customize the design including background image
          </ListItem>
          <ListItem>
            <IconCheck />
            Send a home value range instead of a full report
          </ListItem>
          <ListItem>
            <IconCheck />
            Create unlimited pages to target neighborhoods
          </ListItem>
          <ListItem>
            <IconCheck />
            Create pages that also engage buyers
          </ListItem>
        </List>
      </ListContainer>
      <Text align="center">
        <TryAttract href={config.casRootUrl} target="_blank">
          Try Cloud Attract free <IconArrowForward />
        </TryAttract>
      </Text>
    </Section>
  );
}

export default SettingsLeadGeneration;
